import type { Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { Editor } from '@tiptap/core';
import { Node } from '@tiptap/pm/model';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { takeUntil, type Observable } from 'rxjs';

interface IndexEntry {
  level: number;
  title: string;
  node: Node;
  pos: number;
}

@customElement('dla-index')
export class DLAIndex extends ChemistryLitElement {
  @property() editorState: Maybe<Editor> = null;
  @property() onChange: Maybe<Observable<any>> = null;

  override connectedCallback() {
    super.connectedCallback();

    this.onChange?.pipe(takeUntil(this.disconnected)).subscribe(() => {
      this.requestUpdate();
    });
  }

  override render() {
    const index: IndexEntry[] = [];
    this.editorState?.state.doc.descendants((node, pos) => {
      if (node.toString().startsWith('heading') && node.attrs.level) {
        index.push({ level: node.attrs.level, title: node.textContent, node, pos });
      }
      return true;
    });

    if (index.length === 0) {
      return html` <x-text ${css({ color: this.colors.neutral })}> No headings yet... </x-text> `;
    }

    return html`
      <x-flex space="sm">
        ${index.map(
          (e, i) =>
            html`
              <x-text
                ${acss({
                  marginTop: e.level === 1 && i !== 0 ? this.spacing.xs : undefined,
                  marginLeft: `${(e.level - 1) * 20}px`,
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                })}
                @click=${() => {
                  const node = this.editorState?.view.nodeDOM(e.pos) as HTMLElement;
                  const top = node.getBoundingClientRect().top;

                  this.dispatchEvent(
                    new CustomEvent('request-scroll', {
                      detail: {
                        value: top,
                      },
                    }),
                  );
                }}
              >
                • ${e.title}
              </x-text>
            `,
        )}
      </x-flex>
    `;
  }
}
